import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import {
    submitLunchPlanWaiver,
    selectWaiverById,
    selectSubmitLoading
} from '../../store/lunchPlanWaiver/lunchPlanWaiverSlice';
import { handleImageUpload } from '../../api/uploads';
import { getEffectiveStudentId } from '../../utilities/teacherModulePermissions';

const LunchPlanWaiverFormComp = ({ onSubmit, onCancel, onBack, remarks, defaultData }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const existingWaiver = useSelector((state) => selectWaiverById(state, id));
    const submitLoading = useSelector(selectSubmitLoading);
    let profileData = useSelector((state) => state.setting.setting.user_data);
    profileData = profileData ? JSON.parse(profileData) : null;

    const signaturePadRef = useRef(null);
    // Tracks whether the parent has actually drawn on the pad (set via
    // onEnd below, which only fires for a real pointer/touch stroke - never
    // from the programmatic fromDataURL() load in the effect below that
    // draws an existing signature onto it). Used at submit time to decide
    // whether there's a new signature to upload, rather than just checking
    // whether the pad looks non-empty - which it will, for as long as an
    // existing signature was successfully pre-loaded onto it, even though
    // nothing new was drawn.
    const signatureDirtyRef = useRef(false);

    // submitLoading (Redux) only flips true once the submitLunchPlanWaiver
    // thunk actually dispatches — it doesn't cover the signature upload that
    // happens before that. A fast double-click inside that gap could fire two
    // uploads and two submits, so this local flag disables the button the
    // instant validation passes, synchronously, before any of that async work.
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        reason: '',
        parents_signature: '',
        parent_signature_date: ''
    });

    useEffect(() => {
        if (isEditMode && existingWaiver) {
            setFormData({
                reason: existingWaiver.reason || '',
                parents_signature: existingWaiver.parents_signature || '',
                parent_signature_date: existingWaiver.parent_signature_date || ''
            });
        }
    }, [isEditMode, existingWaiver]);

    // Draws an existing signature URL directly onto the pad on edit, so it
    // shows up already signed instead of a blank pad next to a separate
    // static preview image. Fetches it as a blob first and hands the pad a
    // same-origin blob: URL rather than a plain cross-origin one - drawing
    // a cross-origin image straight onto the canvas permanently taints it,
    // and every later toDataURL() call (i.e. re-saving this pad) then
    // throws a SecurityError instead of producing an image, even for a
    // resubmit that never touched the signature. Deliberately no fallback
    // to a direct (non-blob) draw if this fetch fails - confirmed
    // elsewhere in this app that doing so, while it does make the pad
    // preview correctly, still leaves toDataURL() throwing on a later
    // submit and breaks it entirely. A signature that doesn't preview here
    // is a much smaller problem than a submission that hard-fails.
    useEffect(() => {
        const url = existingWaiver?.parents_signature;
        if (!isEditMode || !url) return;

        let cancelled = false;
        let attempts = 0;
        let timeoutId;

        const tryDraw = () => {
            attempts += 1;
            const pad = signaturePadRef.current;
            if (!pad) {
                if (attempts < 10) timeoutId = setTimeout(tryDraw, 150);
                return;
            }
            if (!pad.isEmpty()) return;

            fetch(url)
                .then((res) => {
                    if (!res.ok) throw new Error(`Signature fetch failed: ${res.status}`);
                    return res.blob();
                })
                .then((blob) => {
                    if (cancelled || !signaturePadRef.current || !signaturePadRef.current.isEmpty()) return;
                    const objectUrl = URL.createObjectURL(blob);
                    signaturePadRef.current.fromDataURL(objectUrl);
                    setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
                })
                .catch((e) => {
                    console.log('Could not load existing signature - leaving the pad blank:', e);
                });
        };

        timeoutId = setTimeout(tryDraw, 100);
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [isEditMode, existingWaiver?.parents_signature]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleClearSignature = () => {
        if (signaturePadRef.current) {
            signaturePadRef.current.clear();
        }
        signatureDirtyRef.current = false;
        // Previously left formData.parents_signature untouched, so
        // clearing only ever cleared the pad visually - the old URL was
        // still what got submitted.
        setFormData(prev => ({ ...prev, parents_signature: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) {
            return;
        }

        // Validation
        if (!formData.reason) {
            Swal.fire({
                icon: 'warning',
                title: 'Required Field',
                text: 'Please provide a reason for the waiver.'
            });
            return;
        }

        const hasSignaturePadContent = signaturePadRef.current && !signaturePadRef.current.isEmpty();
        const hasExistingSignature = isEditMode && existingWaiver?.parents_signature;

        if (!hasSignaturePadContent && !hasExistingSignature) {
            Swal.fire({
                icon: 'warning',
                title: 'Signature Required',
                text: 'Please provide parent signature.'
            });
            return;
        }

        if (!formData.parent_signature_date) {
            Swal.fire({
                icon: 'warning',
                title: 'Date Required',
                text: 'Please provide signature date.'
            });
            return;
        }

        setIsSubmitting(true);

        try {
            const submitData = { ...formData };

            // Only re-upload if the pad has something the parent actually
            // drew (signatureDirtyRef) - not just "the pad looks
            // non-empty", which it will for as long as an existing
            // signature is loaded onto it (see the effect above), even
            // when nothing new was drawn.
            if (signatureDirtyRef.current && hasSignaturePadContent) {
                try {
                    const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
                    const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
                    const signatureFile = new File([signatureBlob], `parent_signature_${Date.now()}.png`, { type: 'image/png' });
                    const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');
                    submitData.parents_signature = signatureUrl;
                } catch (uploadError) {
                    // A tainted canvas (or any other upload failure)
                    // shouldn't hard-fail the whole submission - fall back
                    // to whatever signature was already on file.
                    console.error('Could not upload signature - keeping the existing value:', uploadError);
                    if (hasExistingSignature) {
                        submitData.parents_signature = existingWaiver.parents_signature;
                    }
                }
            } else if (hasExistingSignature) {
                submitData.parents_signature = existingWaiver.parents_signature;
            }

            if (isEditMode) {
                submitData.id = id;
            }

            submitData.student_id = getEffectiveStudentId(profileData);

            // Show loader during submission
            Swal.fire({
                title: 'Processing...',
                html: 'Saving lunch plan waiver...',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // When embedded in the profile page's Registration Packet tab, the
            // host page passes its own onSubmit that dispatches, shows its own
            // success message, and refreshes the tab's data in place - let it
            // own that instead of also dispatching/navigating away here (which
            // used to happen unconditionally and silently kicked the parent
            // out to /dashboard/lunch-plan-waiver/list even when they were
            // filling this in from inside their profile). The standalone
            // add/edit routes never pass onSubmit, so they're unaffected.
            if (onSubmit) {
                await onSubmit(submitData);
                Swal.close();
                signatureDirtyRef.current = false;
            } else {
                await dispatch(submitLunchPlanWaiver(submitData)).unwrap();

                Swal.close();
                signatureDirtyRef.current = false;

                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `Lunch Plan Waiver ${isEditMode ? 'updated' : 'created'} successfully!`
                });

                navigate('/dashboard/lunch-plan-waiver/list');
            }
        } catch (error) {
            Swal.close();
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error || 'Failed to submit waiver'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container>
            <Row>
                <Col sm="12">
                    <Card>
                        {remarks &&
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <div className="header-title">
                                    <h4 className="card-title">
                                        {isEditMode ? 'Edit' : 'Create'} {remarks}
                                    </h4>
                                </div>
                            </Card.Header>
                        }
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                {/* Waiver Information */}
                                <div className="mb-5 pb-4 border-bottom">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                                            <i className="material-symbols-outlined">restaurant</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">Waiver Information</h5>
                                    </div>
                                    <p className="text-muted mb-4">Only complete this if you are opting your child OUT of the daycare's lunch plan.</p>
                                    <Row className="mb-3">
                                        <Col md="12">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Reason for Lunch Plan Waiver <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    placeholder="e.g., Child prefers home lunch due to allergy concerns."
                                                    value={formData.reason}
                                                    onChange={(e) => handleInputChange('reason', e.target.value)}
                                                    required
                                                />
                                                <Form.Text className="text-muted">
                                                    Please provide a clear reason why you are opting out of the daycare lunch program.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Signature Section */}
                                <div className="mb-2">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="text-white rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px', backgroundColor: '#6f42c1' }}>
                                            <i className="material-symbols-outlined">draw</i>
                                        </div>
                                        <h5 className="mb-0 fw-bold text-dark">Parent Signature</h5>
                                    </div>
                                    <Row className="mb-3">
                                        <Col md="12">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Signature <span className="text-danger">*</span></Form.Label>
                                                <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px', borderRadius: '8px' }}>
                                                    <SignatureCanvas
                                                        ref={signaturePadRef}
                                                        clearOnResize={false}
                                                        onEnd={() => { signatureDirtyRef.current = true; }}
                                                        canvasProps={{
                                                            className: 'signature-canvas',
                                                            style: { width: '100%', height: '100%' }
                                                        }}
                                                    />
                                                </div>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="mt-2"
                                                    onClick={handleClearSignature}
                                                    type="button"
                                                >
                                                    <i className="material-symbols-outlined me-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>ink_eraser</i>
                                                    Clear Signature
                                                </Button>
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group className="mb-3">
                                                <Form.Label>Signature Date <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={formData.parent_signature_date}
                                                    onChange={(e) => handleInputChange('parent_signature_date', e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Submit Buttons */}
                                <div className="d-flex justify-content-end gap-2 pt-3 mt-2" style={{ borderTop: '1px solid #eef0f3' }}>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => navigate('/dashboard/lunch-plan-waiver/list')}
                                        disabled={isSubmitting || submitLoading}
                                    >
                                        <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>close</i>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isSubmitting || submitLoading}
                                    >
                                        {isSubmitting || submitLoading ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <i className="material-symbols-outlined me-1" style={{ verticalAlign: 'middle' }}>check_circle</i>
                                                {isEditMode ? 'Update' : 'Submit'} Waiver
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default LunchPlanWaiverFormComp;
